import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { TrendUp, TrendDown, Target, Lightbulb, ArrowRight } from '@phosphor-icons/react';

/* Industry benchmarks (avg for e-commerce, sourced from Sistrix/Backlinko 2024). */
const BENCHMARKS = {
  ctr: { good: 0.04, ok: 0.025, label: 'Klikkrate' },
  position: { good: 5, ok: 10, label: 'Snittposisjon' },
};

function Grade({ value, type }) {
  const b = BENCHMARKS[type];
  if (b == null || value == null) return null;
  let grade, color, msg;
  if (type === 'ctr') {
    if (value >= b.good) { grade = 'A'; color = 'text-success'; msg = 'Bedre enn snittet'; }
    else if (value >= b.ok) { grade = 'B'; color = 'text-warning'; msg = 'Litt under snittet'; }
    else { grade = 'D'; color = 'text-destructive'; msg = 'Klart under snittet — meta-titler trenger jobb'; }
  } else {
    if (value <= b.good) { grade = 'A'; color = 'text-success'; msg = 'På topp 5 — utmerket'; }
    else if (value <= b.ok) { grade = 'B'; color = 'text-warning'; msg = 'Topp 10 — godt utgangspunkt'; }
    else { grade = 'D'; color = 'text-destructive'; msg = 'Side 2 eller lenger — innhold + lenker trengs'; }
  }
  return (
    <div>
      <span className={`font-heading text-3xl font-bold ${color}`}>{grade}</span>
      <p className="text-xs text-muted-foreground mt-1">{msg}</p>
    </div>
  );
}

function Stat({ label, value, hint, grade }) {
  return (
    <div className="bg-muted/30 rounded-md p-4 border border-border/50">
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="font-heading text-2xl font-bold mb-1">{value}</p>
      {hint && <p className="text-xs text-muted-foreground mb-2">{hint}</p>}
      {grade}
    </div>
  );
}

export default function SeoReportTab({ data, hidePosts, onSendToAutoGuard, navigate }) {
  if (!data) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-muted-foreground">
          Ingen data ennå. Gå til <button className="text-primary underline" onClick={() => onSendToAutoGuard?.('switch-to-detalj')}>Detaljer</button>-fanen og trykk «Oppdater data» for å hente fra Google.
        </CardContent>
      </Card>
    );
  }

  const totals = data.totals || {};
  const ctr = totals.ctr || 0;
  const avgPos = data.quick_wins?.low_ctr?.length
    ? (data.quick_wins.low_ctr.reduce((a, r) => a + (r.position || 0), 0) / data.quick_wins.low_ctr.length)
    : null;

  // Filter buckets by hidePosts
  const filt = (rows) => hidePosts ? (rows || []).filter((r) => !r.is_post) : (rows || []);
  const lowCtr = filt(data.quick_wins?.low_ctr).slice(0, 5);
  const striking = filt(data.quick_wins?.striking_distance).slice(0, 5);

  // Calculate top 5 opportunities (by potential clicks gained)
  const opportunities = [...lowCtr, ...striking]
    .filter((r) => r.product_id)
    .sort((a, b) => (b.priority_score || b.impressions) - (a.priority_score || a.impressions))
    .slice(0, 5);

  return (
    <div className="space-y-6" data-testid="seo-report-tab">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl font-bold mb-1">SEO-rapporten din</h2>
        <p className="text-sm text-muted-foreground">
          Slik gjør butikken din det i Google akkurat nå — og hva du bør prioritere.
        </p>
      </div>

      {/* Karakter-bok */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendUp size={20} /> Karakterboka di
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Stat
              label="Klikk fra Google (siste 30 dager)"
              value={totals.clicks?.toLocaleString('nb-NO') || '0'}
              hint={`${totals.impressions?.toLocaleString('nb-NO') || 0} visninger totalt`}
            />
            <Stat
              label="Klikkrate (CTR)"
              value={`${(ctr * 100).toFixed(2)}%`}
              hint="Bransjesnitt for nettbutikk: 3–5%"
              grade={<Grade value={ctr} type="ctr" />}
            />
            <Stat
              label="Snittposisjon (gjennomsnitt for sider med lav CTR)"
              value={avgPos ? avgPos.toFixed(1) : 'N/A'}
              hint="Posisjon 1–5 = topp av side 1"
              grade={avgPos ? <Grade value={avgPos} type="position" /> : null}
            />
          </div>
        </CardContent>
      </Card>

      {/* Topp 5 muligheter */}
      <Card>
        <CardHeader className="flex-row items-start justify-between gap-2">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Target size={20} /> Topp 5 muligheter akkurat nå
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Sortert etter potensial — disse gir mest tilbake per AI-kjøring.
            </p>
          </div>
          {opportunities.length > 0 && (
            <Button size="sm" onClick={() => onSendToAutoGuard?.(opportunities.map((o) => o.product_id))} className="gap-1" data-testid="report-send-all">
              Send alle 5 til AutoGuard <ArrowRight size={14} />
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {opportunities.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Ingen åpenbare muligheter — enten har du allerede god SEO eller for lite data ennå.
            </p>
          ) : (
            <div className="divide-y divide-border">
              {opportunities.map((r, i) => {
                const reason = r.position > 10
                  ? `Ligger på posisjon ${r.position?.toFixed(1)} (side 2). En liten push kan flytte den til topp 10.`
                  : `${r.impressions} visninger men kun ${r.clicks} klikk (${(r.ctr * 100).toFixed(1)}% CTR). Bedre meta-tittel = flere klikk.`;
                return (
                  <div key={i} className="py-3 flex items-center justify-between gap-3 cursor-pointer hover:bg-muted/20 -mx-2 px-2 rounded"
                       onClick={() => navigate?.(`/products/${r.product_id}`)}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{r.title || r.url || `Produkt #${r.product_id}`}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">{reason}</p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">
                      {r.impressions?.toLocaleString('nb-NO')} vis.
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Sammenligning */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lightbulb size={20} /> Sammenlignet med andre nettbutikker
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <BenchmarkRow
              label="Klikkrate"
              you={`${(ctr * 100).toFixed(2)}%`}
              benchmark="3.5%"
              better={ctr >= 0.035}
            />
            <BenchmarkRow
              label="Sider med 0 klikk"
              you={data.quick_wins?.zero_clicks?.length || 0}
              benchmark="under 20% av totalt"
              better={(data.quick_wins?.zero_clicks?.length || 0) < (totals.total_pages || 1) * 0.2}
            />
            <BenchmarkRow
              label="Vinnere (sider som rangerer godt)"
              you={data.quick_wins?.winners?.length || 0}
              benchmark="minst 10% av totalt"
              better={(data.quick_wins?.winners?.length || 0) >= (totals.total_pages || 1) * 0.1}
            />
          </div>
        </CardContent>
      </Card>

      {/* Konkrete handlinger */}
      <Card className="border-primary/30">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Target size={20} /> Hva du bør gjøre denne uken
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-3 text-sm">
            {(data.quick_wins?.low_ctr?.length > 5) && (
              <li className="flex items-start gap-2">
                <span className="text-destructive font-bold mt-0.5">1.</span>
                <span>
                  <strong>{filt(data.quick_wins.low_ctr).filter((r) => r.product_id).length} produkter</strong> har høye visninger men lav CTR.
                  Send dem til AutoGuard for nye meta-titler — størst gevinst per innsats.
                </span>
              </li>
            )}
            {(data.quick_wins?.striking_distance?.length > 0) && (
              <li className="flex items-start gap-2">
                <span className="text-warning font-bold mt-0.5">2.</span>
                <span>
                  <strong>{filt(data.quick_wins.striking_distance).filter((r) => r.product_id).length} produkter</strong> ligger på posisjon 4–15 (akkurat utenfor topp 3).
                  Legg til 1–2 interne lenker fra andre produkter for å pushe dem opp.
                </span>
              </li>
            )}
            {(data.quick_wins?.zero_clicks?.length > 0) && (
              <li className="flex items-start gap-2">
                <span className="text-muted-foreground font-bold mt-0.5">3.</span>
                <span>
                  <strong>{filt(data.quick_wins.zero_clicks).filter((r) => r.product_id).length} produkter</strong> har visninger men 0 klikk.
                  Sjekk om tittelen matcher det folk søker på, eller om bildet ser uinteressant ut.
                </span>
              </li>
            )}
            {(data.quick_wins?.query_goldmines?.length > 0) && (
              <li className="flex items-start gap-2">
                <span className="text-primary font-bold mt-0.5">4.</span>
                <span>
                  Du rangerer på <strong>{data.quick_wins.query_goldmines.length} søkeord</strong> som folk leter etter, men du eier dem ikke ennå.
                  Vurder å lage en kategoriside eller blogginnlegg som svarer direkte på disse spørsmålene.
                </span>
              </li>
            )}
            {(data.quick_wins?.winners?.length > 0) && (
              <li className="flex items-start gap-2">
                <span className="text-success font-bold mt-0.5">5.</span>
                <span>
                  <strong>Ikke rør</strong> de {data.quick_wins.winners.length} sidene som rangerer på topp 3 — AutoGuard er allerede satt til å hoppe over disse.
                </span>
              </li>
            )}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}

function BenchmarkRow({ label, you, benchmark, better }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <span className="text-sm">{label}</span>
      <div className="flex items-center gap-3 text-sm">
        <span className="text-muted-foreground">Bransjesnitt: <span className="font-mono">{benchmark}</span></span>
        <span className={`font-mono font-semibold ${better ? 'text-success' : 'text-destructive'}`}>
          Du: {you}
        </span>
        {better ? <TrendUp size={16} className="text-success" /> : <TrendDown size={16} className="text-destructive" />}
      </div>
    </div>
  );
}
